import { gql, useQuery, useSubscription } from "@apollo/client";
import { newTransactionSubscription } from "../../../Transaction/FindTransactions";
import { 
    FindAllTransactionsAdminQuery, 
    FindAllTransactionsAdminQueryVariables, 
    Transaction 
} from "../../../../../schemaTypes";
import { useCallback } from "react";

export const findAllTransactionsAdminQuery = gql`
  query FindAllTransactionsAdmin(
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $duration: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllTransactionsAdmin(
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      duration: $duration
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        wallet {
          id
          balance
          owner {
            id
            customer{
              firstname
              lastname
            }
          }
          createdAt
          updatedAt
        }
        recipientWallet {
          id
          balance
          currency
          owner {
            admin {
              merchantProfile {
                companyName
              }
            }
          }
          createdAt
          updatedAt
        }
        bankPreference{
          id
        }
        offer {
          id
          title
          description
          bannerUrl
          category {
            id
            name
            imgSrc
            createdBy{
              id
              role
            }
            createdAt
            updatedAt
          }
          offerType
          discountType
          discountValue
          partnerVoucher
          referralUrl
          status
          usageLimitPerUser
          totalUsageLimit
          startDate
          endDate
          community{
            id
            name
            alias
          }
          redemptions
          revenueGenerated
          clickThroughs
          featured
          createdBy{
            id
            role
            admin {
              id
              shortId
              firstname
              lastname
              email
              phone
              merchantProfile {
                id
                businessName
                businessType
                regCountry
                businessRegNumber
                taxId
                address
                addressAlt
                city
                country
                postcode
                companyName
                username
                industry {
                  id
                  name
                  createdBy{
                    id
                    role
                  }
                  createdAt
                  updatedAt
                }
                companyLocation
                logoUrl
                bannerUrl
                description
                commissionRate
                createdAt
                updatedAt
              }
              createdAt
              updatedAt
            }
          }
          # njoyCode {
          #   id
          #   code
          #   redeemedAt
          #   createdAt
          #   updatedAt
          # }
          createdAt
          updatedAt
        }
        njoyCode
        partnerVoucher
        user {
          id
          customer {
            firstname
            lastname
          }
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

newTransactionSubscription;

export interface WithFindAllTransactionsAdmin{
  transactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: FindAllTransactionsAdminQueryVariables;
  children: (data: WithFindAllTransactionsAdmin) => React.ReactElement | null;
}

export const FindAllTransactionsAdminController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllTransactionsAdminQuery,
    FindAllTransactionsAdminQueryVariables
  >(findAllTransactionsAdminQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  // Subscription for new transactions (optional, adjust as needed)
  useSubscription(newTransactionSubscription);

  const transactions = data?.findAllTransactionsAdmin.data || [];
  const page = data?.findAllTransactionsAdmin?.page || variables.page;
  const pageSize = data?.findAllTransactionsAdmin?.pageSize || variables.pageSize;
  const total = data?.findAllTransactionsAdmin?.total || 0;
  const totalPages = data?.findAllTransactionsAdmin?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllTransactionsAdmin) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllTransactionsAdmin: {
            ...fetchMoreResult.findAllTransactionsAdmin,
            data: [
              ...(prev?.findAllTransactionsAdmin?.data || []),
              ...(fetchMoreResult.findAllTransactionsAdmin.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any,
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};